import React, { FC, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button, Grid, Paper } from '@mui/material'
import {
  DataGridPremium,
  useGridApiRef,
  GridPaginationModel,
} from '@mui/x-data-grid-premium'
import { DataLoad } from 'src/components/ui'
import { useUserContractorListRequest } from 'src/hooks/api'
import { useQueryParams } from 'src/hooks/ui'
import { SearchParamsKeys } from 'src/enums'
import { defaultRowsValuePerPage, defaultPage, defaultPerPage } from 'src/config'
import UserContractorColumns from 'src/components/columns/userContractor'

const columns = [
  UserContractorColumns.Name,
  UserContractorColumns.Email,
  UserContractorColumns.CreatedAt,
  UserContractorColumns.Phone,
  UserContractorColumns.LastActive,
  UserContractorColumns.LastAction,
]

interface UsersOfContractorProps {
  contractorId: number;
}

export const UsersOfContractor: FC<UsersOfContractorProps> = ({
  contractorId,
}) => {
  const navigate = useNavigate()
  const apiRef = useGridApiRef()
  const [params, setQueryParams] = useQueryParams({
    page: `${defaultPage}`,
    rowsPerPage: `${defaultPerPage}`,
  })
  const { tab, ...restParams } = params
  const { data, isInitialLoading, isError, error } = useUserContractorListRequest({
    params: {
      ...restParams,
      contractorId,
    },
  })

  const [rowCountState, setRowCountState] = useState(data?.count || 0)

  useEffect(() => {
    setRowCountState((prev) =>
      data?.count !== undefined ? data.count : prev
    )
  }, [data?.count])

  const handleChangePaginationModel = async (pagination: GridPaginationModel): Promise<void> => {
    const { page, pageSize } = pagination
    const newPage = page + 1
    setQueryParams({
      [SearchParamsKeys.Page]: `${newPage}`,
      [SearchParamsKeys.RowsPerPage]: `${pageSize}`,
    }, [
      SearchParamsKeys.Page,
      SearchParamsKeys.RowsPerPage,
    ])
  }

  const page = params['page'] ? parseInt(params['page'], 10) - 1 : defaultPage - 1
  const pageSize = params['rowsPerPage']
    ? parseInt(params['rowsPerPage'], 10)
    : defaultPerPage

  const onAddUser = async () => {
    await setQueryParams({ [SearchParamsKeys.Contractor]: `${contractorId}` })
    navigate('/users/new')
  }

  return (
    <Grid container direction="column" justifyContent="stretch" spacing={2} wrap="nowrap">
      <Grid item>
        <Button variant="contained" onClick={onAddUser}>Add User</Button>
      </Grid>
      <Grid item minWidth={0}>
        <DataLoad
          isLoading={false}
          isError={isError}
          errorMessage={error?.message}
        >
          <Paper sx={{ flexGrow: 1, minHeight: '200px', width: '100%' }}>
            <DataGridPremium
              apiRef={apiRef}
              rows={data?.rows || []}
              pagination
              autoHeight
              disableRowSelectionOnClick
              loading={isInitialLoading}
              columns={columns}
              rowCount={rowCountState}
              paginationModel={{
                page,
                pageSize,
              }}
              paginationMode="server"
              pageSizeOptions={defaultRowsValuePerPage}
              onPaginationModelChange={handleChangePaginationModel}
              localeText={{
                columnMenuSortAsc: 'Sort A-Z',
                columnMenuSortDesc: 'Sort Z-A',
              }}
              sx={{
                width: '100%',
                '& .MuiDataGrid-columnHeaderTitle': {
                  whiteSpace: 'normal',
                  lineHeight: 1,
                  fontSize: '13px',
                  fontWeight: 400,
                  color: 'rgba(0, 0, 0, 0.6)',
                  overflow: 'visible',
                },
              }}
            />
          </Paper>
        </DataLoad>
      </Grid>
    </Grid>
  )
}
